import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ShieldCheck, Clock, MapPin, Briefcase, Euro, CalendarCheck } from 'lucide-react';
import { heroProfiles, networkFaces } from '../data/mock';

const badge = (delay) => ({
  initial: { opacity: 0, y: 24, scale: 0.9 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { delay, type: 'spring', stiffness: 120, damping: 18 },
});

const Fact = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl bg-white/[0.04] border border-white/[0.07] px-3.5 py-3">
    <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold flex items-center gap-1.5"><Icon className="w-3 h-3 text-amber-400" /> {label}</p>
    <p className="mt-1 text-white font-semibold text-sm">{value}</p>
  </div>
);

const CandidateCard = ({ p, index }) => (
  <motion.div
    key={p.title}
    initial={{ opacity: 0, rotateY: -35, x: 120, z: -200 }}
    animate={{ opacity: 1, rotateY: 0, x: 0, z: 0 }}
    exit={{ opacity: 0, rotateY: 30, x: -120, z: -200 }}
    transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
    style={{ transformStyle: 'preserve-3d' }}
    className="absolute inset-0"
    data-testid="hero-candidate-card"
  >
    <div className="relative h-full w-full rounded-[1.75rem] p-[1.5px] bg-gradient-to-br from-amber-400/70 via-violet-500/40 to-amber-400/30 shadow-[0_40px_80px_-30px_rgba(124,58,237,0.6)]">
      <div className="relative h-full w-full rounded-[calc(1.75rem-1.5px)] bg-[#12101B] overflow-hidden p-6 sm:p-7 flex flex-col">
        <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-violet-600/30 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />

        <div className="relative flex items-start justify-between gap-3">
          <div className="flex items-start gap-4 min-w-0">
            <div className="shrink-0 relative">
              <img src={p.image} alt={p.title} className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl object-cover" data-testid="hero-candidate-avatar" />
              <span className="absolute -inset-[2px] rounded-[1.1rem] border border-amber-400/60 pointer-events-none" />
              <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-[#12101B]" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">Kandidatenprofil · {p.tag}</p>
              <h3 className="mt-1.5 font-display text-2xl sm:text-3xl font-extrabold text-white leading-tight" data-testid="hero-candidate-title">{p.title}</h3>
              <p className="mt-1 text-slate-400 text-sm inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {p.city} · Index #{String(index + 1).padStart(3, '0')}</p>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="relative w-16 h-16">
              <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                <circle cx="18" cy="18" r="15.5" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
                <motion.circle cx="18" cy="18" r="15.5" fill="none" stroke="url(#matchGrad)" strokeWidth="3" strokeLinecap="round"
                  strokeDasharray="97.4" initial={{ strokeDashoffset: 97.4 }} animate={{ strokeDashoffset: 97.4 - (97.4 * p.match) / 100 }} transition={{ duration: 1.2, delay: 0.4, ease: 'easeOut' }} />
                <defs><linearGradient id="matchGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#FDE68A" /><stop offset="100%" stopColor="#F59E0B" /></linearGradient></defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-display text-base font-extrabold text-white leading-none">{p.match}%</span>
                <span className="text-[9px] uppercase tracking-wider text-slate-500">Match</span>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mt-6 grid grid-cols-2 gap-2.5" data-testid="hero-candidate-facts">
          <Fact icon={Briefcase} label="Erfahrung" value={p.experience} />
          <Fact icon={Euro} label="Gehaltswunsch" value={p.salary} />
          <Fact icon={CalendarCheck} label="Verfügbar" value={p.availability} />
          <Fact icon={Clock} label="Geliefert in" value={p.time} />
        </div>

        <div className="relative mt-5 flex flex-wrap gap-2">
          {p.skills.map((s) => (
            <span key={s} className="text-xs font-medium text-slate-200 bg-white/5 border border-white/10 rounded-full px-3 py-1">{s}</span>
          ))}
        </div>

        <div className="relative mt-auto pt-5 flex items-center justify-between border-t border-white/[0.07]">
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-emerald-300"><CheckCircle2 className="w-4 h-4" /> Interviewt & gebrieft</span>
        </div>
      </div>
    </div>
  </motion.div>
);

const HeroCandidateCards = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    heroProfiles.forEach((pr) => { const img = new Image(); img.src = pr.image; });
  }, []);

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % heroProfiles.length), 4500);
    return () => clearInterval(t);
  }, [active]);

  const p = heroProfiles[active];
  const next = heroProfiles[(active + 1) % heroProfiles.length];
  const after = heroProfiles[(active + 2) % heroProfiles.length];

  return (
    <div className="relative mx-auto w-full max-w-[440px] lg:max-w-none pb-16 sm:pt-12" data-testid="hero-portrait">
      <div className="absolute -inset-10 bg-violet-600/30 blur-[90px] rounded-full pointer-events-none" />
      <div className="absolute -inset-4 bg-amber-400/10 blur-[60px] rounded-full pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
        className="relative z-10 float-slow"
        style={{ perspective: 1400 }}
      >
        <motion.div
          animate={{ rotateY: [-6, 6, -6], rotateX: [4, -3, 4] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          style={{ transformStyle: 'preserve-3d' }}
          className="relative h-[440px] sm:h-[490px]"
          data-testid="hero-card-stage"
        >
          <div className="absolute inset-0 rounded-[1.75rem] bg-[#15121F] border border-white/[0.06] opacity-60" style={{ transform: 'translateZ(-140px) translateX(44px) translateY(28px) rotateY(-10deg)' }}>
            <p className="absolute top-6 left-6 text-xs text-slate-600 font-semibold">{after.title}</p>
          </div>
          <div className="absolute inset-0 rounded-[1.75rem] bg-[#171324] border border-white/[0.08] opacity-80" style={{ transform: 'translateZ(-70px) translateX(22px) translateY(14px) rotateY(-5deg)' }}>
            <p className="absolute top-6 left-6 text-xs text-slate-500 font-semibold">{next.title}</p>
          </div>
          <AnimatePresence mode="popLayout" initial={false}>
            <CandidateCard key={p.title} p={p} index={active} />
          </AnimatePresence>
        </motion.div>
      </motion.div>

      <div className="relative z-30 mt-2 flex justify-center gap-1" data-testid="hero-profile-dots">
        {heroProfiles.map((_, i) => (
          <button key={i} onClick={() => setActive(i)} aria-label={`Profil ${i + 1}`} className="py-3 px-0.5 group" data-testid={`hero-profile-dot-${i}`}>
            <span className={`block h-1 rounded-full transition-all duration-500 ${i === active ? 'w-8 bg-amber-400' : 'w-3 bg-white/25 group-hover:bg-white/50'}`} />
          </button>
        ))}
      </div>

      <motion.div {...badge(1.0)} className="hidden sm:block absolute top-0 -left-3 sm:-left-10 z-20" data-testid="hero-badge-delivered">
        <div className="glass-dark rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl">
          <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-400" /></div>
          <div>
            <p className="text-white text-sm font-semibold leading-tight">Kandidat geliefert</p>
            <p className="text-emerald-400 text-xs font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> nach {p.time}</p>
          </div>
        </div>
      </motion.div>

      <motion.div {...badge(1.2)} className="hidden sm:block absolute bottom-20 -right-3 sm:-right-10 z-20" data-testid="hero-badge-guarantee">
        <div className="glass-dark rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl border-amber-400/30">
          <div className="w-9 h-9 rounded-full bg-amber-400/15 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-amber-400" /></div>
          <div>
            <p className="text-white text-sm font-semibold leading-tight">100 % Nachbesetzung</p>
            <p className="text-slate-400 text-xs">kostenlos · ohne Risiko</p>
          </div>
        </div>
      </motion.div>

      <motion.div {...badge(1.4)} style={{ x: '-50%' }} className="absolute -bottom-6 left-1/2 z-20 w-max" data-testid="hero-badge-network">
        <div className="glass-dark rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl">
          <div className="flex -space-x-2.5">
            {networkFaces.map((src, i) => <img key={i} src={src} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-[#13111C]" />)}
          </div>
          <div>
            <p className="text-white text-sm font-semibold leading-tight">4.200+ Kandidaten</p>
            <p className="text-slate-400 text-xs">im exklusiven Netzwerk</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default HeroCandidateCards;
